export type PartnerRole = "PARTNER_OWNER" | "PARTNER_MANAGER" | "GUIDE_OPERATOR" | "VIP_HOST" | "PARTNER_FINANCE";
export type AdminRole = "OWNER_ADMIN" | "SUPPORT_OPERATOR" | "MODERATOR" | "FINANCE_OPERATOR" | "PROVIDER_OPERATOR" | "CONTENT_OPERATOR";
export type PartnerAssetKind = "OFFER" | "EVENT" | "LOCAL_GUIDE" | "VIP_EXPERIENCE" | "CREATOR_CAMPAIGN";
export type PartnerAssetStatus = "DRAFT" | "PENDING_REVIEW" | "APPROVED" | "PAUSED" | "SCHEDULED" | "SUSPENDED" | "EXPIRED" | "OFFBOARDED";
export type ModerationDecision = "APPROVED" | "REMOVED" | "BLOCKED" | "STRIKE_ISSUED" | "ESCALATED";
export type PartnerAdminSupportCaseCategory = "PAYMENT_DISPUTE" | "BOOKING_OR_REFUND" | "PAYOUT" | "CHILD_SAFETY" | "MODERATION" | "APPEAL" | "OTHER_UNRESOLVED";
export type AnalyticsSubject = "ADULT_ACCOUNT" | "CHILD_PROFILE" | "SIGNED_OUT" | "PARTNER" | "SYSTEM";
export type OperationalRecordArea = "REVENUE" | "SUBSCRIPTION" | "BOOKING" | "COMMISSION" | "REFERRAL" | "PAYOUT" | "KYC_TAX" | "REFUND" | "MODERATION" | "REWARD" | "OFFER" | "USAGE";
export type OperationalAlertArea = "PROVIDER_HEALTH" | "API_HEALTH" | "STORE_BILLING" | "AUTH_SESSION" | "PAYOUT_RAIL" | "SUPPORT_DELIVERY" | "BACKUP_RESTORE" | "INCIDENT_RESPONSE";
export type OperationalAlertSeverity = "INFO" | "DEGRADED" | "FAILED" | "URGENT";

const PARTNER_ACTIONS = [
  "listing:create",
  "listing:update",
  "listing:pause",
  "listing:resume",
  "listing:schedule",
  "listing:set_caps",
  "analytics:read_aggregate",
  "payouts:read_own",
  "exports:create_own"
] as const;

const ADMIN_ACTIONS = [
  "users:read",
  "entitlements:mutate",
  "content:moderate",
  "provider_capabilities:mutate",
  "support:mutate",
  "ledgers:read",
  "payouts:mutate",
  "audit:read",
  "partner_governance:mutate"
] as const;

export const PARTNER_PORTAL_ROLE_MATRIX = Object.freeze({
  PARTNER_OWNER: Object.freeze([...PARTNER_ACTIONS]),
  PARTNER_MANAGER: Object.freeze(PARTNER_ACTIONS.filter((action) => !["payouts:read_own", "exports:create_own"].includes(action))),
  GUIDE_OPERATOR: Object.freeze(["listing:update", "listing:pause", "listing:resume", "listing:schedule", "analytics:read_aggregate"]),
  VIP_HOST: Object.freeze(["listing:update", "listing:pause", "listing:resume", "listing:schedule", "listing:set_caps", "analytics:read_aggregate"]),
  PARTNER_FINANCE: Object.freeze(["payouts:read_own", "exports:create_own"])
} satisfies Record<PartnerRole, readonly string[]>);

export const MASTER_ADMIN_ROLE_MATRIX = Object.freeze({
  OWNER_ADMIN: Object.freeze([...ADMIN_ACTIONS]),
  SUPPORT_OPERATOR: Object.freeze(["users:read", "support:mutate", "audit:read"]),
  MODERATOR: Object.freeze(["users:read", "content:moderate", "partner_governance:mutate", "audit:read"]),
  FINANCE_OPERATOR: Object.freeze(["users:read", "ledgers:read", "payouts:mutate", "audit:read"]),
  PROVIDER_OPERATOR: Object.freeze(["provider_capabilities:mutate", "audit:read"]),
  CONTENT_OPERATOR: Object.freeze(["content:moderate", "audit:read"])
} satisfies Record<AdminRole, readonly string[]>);

function requireText(value: string, field: string, maximumLength = 2_000) {
  const normalized = value.trim();
  if (normalized.length === 0) throw new Error(`${field} must not be blank`);
  if (normalized.length > maximumLength) throw new Error(`${field} exceeds the ${maximumLength}-character limit`);
  return normalized;
}

function canonicalIso(value: string, field: string) {
  const parsed = Date.parse(value);
  if (!Number.isFinite(parsed) || new Date(parsed).toISOString() !== value) throw new Error(`${field} must be canonical ISO`);
  return value;
}

function minor(value: number, field: string) {
  if (!Number.isSafeInteger(value) || value < 0) throw new Error(`${field} must use non-negative integer minor units`);
  return value;
}

function currency(value: string) {
  if (!/^[A-Z]{3}$/.test(value)) throw new Error(`Invalid currency: ${value}`);
  return value;
}

function count(value: number, field: string) {
  if (!Number.isSafeInteger(value) || value < 0) throw new Error(`${field} must use a non-negative integer`);
  return value;
}

function requireReadonlyTexts(values: readonly string[], field: string, maximumLength = 500) {
  return Object.freeze(values.map((value, index) => requireText(value.slice(0, maximumLength), `${field}[${index}]`)));
}

function requireNoRawPersonalData(properties: Readonly<Record<string, string>>, field: string) {
  const serialized = JSON.stringify(properties);
  if (/"(?:email|phone|rawIp|fullName|preciseLocation|streetAddress|paymentAccount|walletSeed|taxId)"\s*:/i.test(serialized)) {
    throw new Error(`${field} must not include raw personal data`);
  }
  if (/[^\s@]+@[^\s@]+\.[^\s@]+/.test(serialized)) throw new Error(`${field} must not include raw personal data`);
}

export interface AuditedMutation {
  readonly actorId: string;
  readonly actorRole: AdminRole | PartnerRole;
  readonly action: string;
  readonly reasonCode: string;
  readonly occurredAt: string;
  readonly targetReference: string;
}

export function createAuditedMutation(input: AuditedMutation) {
  requireText(input.actorId, "actorId");
  requireText(input.action, "action");
  requireText(input.reasonCode, "reasonCode");
  requireText(input.targetReference, "targetReference");
  canonicalIso(input.occurredAt, "occurredAt");
  return Object.freeze({ ...input, immutableAuditRequired: true as const, serverAuthority: true as const });
}

export function assertPartnerAction(role: PartnerRole, action: string) {
  if (!(PARTNER_PORTAL_ROLE_MATRIX[role] as readonly string[]).includes(action)) throw new Error(`Partner role ${role} cannot perform ${action}`);
  return true;
}

export function assertAdminAction(role: AdminRole, action: string) {
  if (!(MASTER_ADMIN_ROLE_MATRIX[role] as readonly string[]).includes(action)) throw new Error(`Admin role ${role} cannot perform ${action}`);
  return true;
}

export function createPartnerAsset(input: {
  readonly assetId: string;
  readonly partnerId: string;
  readonly kind: PartnerAssetKind;
  readonly title: string;
  readonly startsAt: string | null;
  readonly endsAt: string | null;
  readonly totalCap: number | null;
  readonly perUserCap: number | null;
  readonly dayTimeRestrictions: readonly string[];
  readonly locationSpecificValidity: readonly string[];
  readonly verifiedForTransactionalPublishing: boolean;
  readonly audit: AuditedMutation;
}) {
  requireText(input.assetId, "assetId");
  requireText(input.partnerId, "partnerId");
  requireText(input.title, "title");
  if (input.startsAt !== null) canonicalIso(input.startsAt, "startsAt");
  if (input.endsAt !== null) canonicalIso(input.endsAt, "endsAt");
  if (input.startsAt !== null && input.endsAt !== null && Date.parse(input.endsAt) <= Date.parse(input.startsAt)) throw new Error("Asset end must follow start");
  if (input.totalCap !== null) minor(input.totalCap, "totalCap");
  if (input.perUserCap !== null) minor(input.perUserCap, "perUserCap");
  assertPartnerAction(input.audit.actorRole as PartnerRole, "listing:create");
  createAuditedMutation(input.audit);
  return Object.freeze({
    ...input,
    status: input.verifiedForTransactionalPublishing ? "PENDING_REVIEW" as const : "DRAFT" as const,
    disappearsFromConsumerWhenPausedOrExpired: true as const,
    historyAndAnalyticsRetained: true as const,
    productionInventoryFabricated: false as const
  });
}

export function transitionPartnerAsset(input: {
  readonly currentStatus: PartnerAssetStatus;
  readonly requestedStatus: PartnerAssetStatus;
  readonly audit: AuditedMutation;
}) {
  createAuditedMutation(input.audit);
  const actionByStatus: Partial<Record<PartnerAssetStatus, string>> = {
    PAUSED: "listing:pause",
    APPROVED: "partner_governance:mutate",
    SCHEDULED: "listing:schedule",
    SUSPENDED: "partner_governance:mutate",
    OFFBOARDED: "partner_governance:mutate"
  };
  const action = actionByStatus[input.requestedStatus] ?? "listing:resume";
  if ((input.audit.actorRole as string).startsWith("PARTNER_") || input.audit.actorRole === "GUIDE_OPERATOR" || input.audit.actorRole === "VIP_HOST") {
    assertPartnerAction(input.audit.actorRole as PartnerRole, action);
    if (["APPROVED", "SUSPENDED", "OFFBOARDED"].includes(input.requestedStatus)) throw new Error("Partner governance transitions require admin authority");
  } else {
    assertAdminAction(input.audit.actorRole as AdminRole, "partner_governance:mutate");
  }
  return Object.freeze({
    from: input.currentStatus,
    to: input.requestedStatus,
    audit: createAuditedMutation(input.audit),
    consumerVisible: input.requestedStatus === "APPROVED" || input.requestedStatus === "SCHEDULED",
    futureTransactionsStopped: input.requestedStatus === "SUSPENDED" || input.requestedStatus === "OFFBOARDED"
  });
}

export function decidePartnerAppeal(input: {
  readonly decisionId: string;
  readonly partnerId: string;
  readonly priorDecisionReference: string;
  readonly appealSequence: number;
  readonly upholdOriginalDecision: boolean;
  readonly audit: AuditedMutation;
}) {
  requireText(input.decisionId, "decisionId");
  requireText(input.partnerId, "partnerId");
  requireText(input.priorDecisionReference, "priorDecisionReference");
  if (input.appealSequence !== 1) throw new Error("Exactly one partner appeal is allowed per decision");
  assertAdminAction(input.audit.actorRole as AdminRole, "partner_governance:mutate");
  return Object.freeze({ ...input, finalAfterAppeal: true as const, audit: createAuditedMutation(input.audit) });
}

export function calculatePartnerSettlement(input: {
  readonly grossMinor: number;
  readonly refundedMinor: number;
  readonly currency: string;
  readonly partnerEnded: boolean;
  readonly lawfulHoldMinor?: number;
}) {
  minor(input.grossMinor, "grossMinor");
  minor(input.refundedMinor, "refundedMinor");
  minor(input.lawfulHoldMinor ?? 0, "lawfulHoldMinor");
  currency(input.currency);
  if (input.refundedMinor > input.grossMinor) throw new Error("Refund cannot exceed gross");
  const retainedMinor = input.grossMinor - input.refundedMinor;
  const platformFeeMinor = Math.floor(retainedMinor * 0.10);
  const legitimateAmountOwedMinor = retainedMinor - platformFeeMinor - (input.lawfulHoldMinor ?? 0);
  return Object.freeze({
    currency: input.currency,
    platformFeeBasisPoints: 1_000,
    platformFeeMinor,
    legitimateAmountOwedMinor,
    cleanOffboardingPaysEarnedMoney: input.partnerEnded,
    relationshipEndWithholdingAllowed: false as const
  });
}

export function projectPartnerAnalytics(input: {
  readonly partnerId: string;
  readonly rows: readonly { readonly listingId: string; readonly views: number; readonly clicks: number; readonly claims: number; readonly redemptions: number; readonly bookings: number; readonly salesMinor: number; readonly revenueMinor: number; readonly customerName?: string | null; readonly customerEmail?: string | null }[];
}) {
  requireText(input.partnerId, "partnerId");
  const byListing = input.rows.map((row) => {
    for (const [field, value] of Object.entries(row).filter(([, value]) => typeof value === "number")) minor(value as number, field);
    return Object.freeze({
      listingId: requireText(row.listingId, "listingId"),
      views: row.views,
      clicks: row.clicks,
      claims: row.claims,
      redemptions: row.redemptions,
      bookings: row.bookings,
      salesMinor: row.salesMinor,
      revenueMinor: row.revenueMinor
    });
  });
  return Object.freeze({ partnerId: input.partnerId, byListing: Object.freeze(byListing), customerNamesIncluded: false as const, customerPiiIncluded: false as const });
}

export function moderateContentWorkflow(input: {
  readonly reportId: string;
  readonly targetKind: "MEMBER_PAGE" | "OFFER" | "GUIDE" | "VIP" | "VOTE" | "REVIEW" | "STREAM";
  readonly reason: "ABUSE" | "FRAUD" | "IMPERSONATION" | "PROHIBITED_CONTENT" | "HARMFUL_LINK" | "SAFETY" | "OTHER";
  readonly evidenceReferences: readonly string[];
  readonly decision: ModerationDecision;
  readonly strikeIssued: boolean;
  readonly appealOffered: boolean;
  readonly audit: AuditedMutation;
}) {
  requireText(input.reportId, "reportId");
  if (input.evidenceReferences.length === 0) throw new Error("Moderation requires retained evidence");
  assertAdminAction(input.audit.actorRole as AdminRole, "content:moderate");
  return Object.freeze({
    ...input,
    evidenceRetentionRequired: true as const,
    humanReviewRequired: true as const,
    audit: createAuditedMutation(input.audit)
  });
}

export function administerGuideWorkflow(input: {
  readonly guideId: string;
  readonly identityVerified: boolean;
  readonly availabilityPublished: boolean;
  readonly cancellationTerms: string;
  readonly audit: AuditedMutation;
}) {
  requireText(input.guideId, "guideId");
  requireText(input.cancellationTerms, "cancellationTerms");
  assertAdminAction(input.audit.actorRole as AdminRole, "partner_governance:mutate");
  return Object.freeze({ ...input, directBookingAllowed: input.identityVerified && input.availabilityPublished, portabilityTakeRateBasisPoints: 1_000 as const, audit: createAuditedMutation(input.audit) });
}

export function administerVipWorkflow(input: {
  readonly vipId: string;
  readonly format: "FIXED_PRICE" | "AUCTION" | "REQUEST_APPROVAL";
  readonly charityMode: boolean;
  readonly charityVerified: boolean;
  readonly capacity: number;
  readonly audit: AuditedMutation;
}) {
  requireText(input.vipId, "vipId");
  minor(input.capacity, "capacity");
  if (input.capacity === 0) throw new Error("VIP capacity must be positive");
  if (input.charityMode && !input.charityVerified) throw new Error("Charity VIP requires verified cause before approval");
  assertAdminAction(input.audit.actorRole as AdminRole, "content:moderate");
  return Object.freeze({
    ...input,
    sellerSideFeeBasisPoints: input.charityMode ? 0 : 1_000,
    buyerFeeMinor: 0 as const,
    paymentRequiredBeforeConfirmation: true as const,
    audit: createAuditedMutation(input.audit)
  });
}

export function administerVoteWorkflow(input: {
  readonly pollId: string;
  readonly accountId: string;
  readonly accountEligible: boolean;
  readonly duplicateAccountSignal: boolean;
  readonly artificialVoteSignal: boolean;
  readonly latestVoteCounts: boolean;
  readonly audit: AuditedMutation;
}) {
  requireText(input.pollId, "pollId");
  requireText(input.accountId, "accountId");
  assertAdminAction(input.audit.actorRole as AdminRole, "content:moderate");
  return Object.freeze({
    ...input,
    voteAccepted: input.accountEligible && !input.duplicateAccountSignal && !input.artificialVoteSignal && input.latestVoteCounts,
    oneVotePerAccount: true as const,
    commentsEnabled: false as const,
    audit: createAuditedMutation(input.audit)
  });
}

export function moderateReviewWorkflow(input: {
  readonly reviewId: string;
  readonly completedTransactionId: string | null;
  readonly source: "PORTABILITY_VERIFIED" | "PROVIDER_ATTRIBUTED";
  readonly reported: boolean;
  readonly remove: boolean;
  readonly audit: AuditedMutation;
}) {
  requireText(input.reviewId, "reviewId");
  if (input.source === "PORTABILITY_VERIFIED" && input.completedTransactionId === null) throw new Error("Portability reviews require completed transaction evidence");
  assertAdminAction(input.audit.actorRole as AdminRole, "content:moderate");
  return Object.freeze({
    ...input,
    verifiedSubmissionOnly: true as const,
    displayAllowed: !input.remove,
    sourceDistinctionPreserved: true as const,
    audit: createAuditedMutation(input.audit)
  });
}

export function createPartnerAdminSupportCase(input: {
  readonly caseId: string;
  readonly accountId: string;
  readonly category: PartnerAdminSupportCaseCategory;
  readonly summary: string;
  readonly replyEmail: string;
  readonly createdAt: string;
}) {
  requireText(input.caseId, "caseId");
  requireText(input.accountId, "accountId");
  requireText(input.summary, "summary");
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(input.replyEmail)) throw new Error("Support reply email is invalid");
  canonicalIso(input.createdAt, "createdAt");
  return Object.freeze({ ...input, status: "OPEN" as const, phoneSupportAvailable: false as const, humanEmailEscalation: true as const });
}

export function createSupportEmailContract(input: {
  readonly caseId: string;
  readonly providerCapabilityActive: boolean;
  readonly outboundDomainVerified: boolean;
  readonly inboundWebhookVerified: boolean;
  readonly retentionPolicyReference: string;
}) {
  requireText(input.caseId, "caseId");
  requireText(input.retentionPolicyReference, "retentionPolicyReference");
  const canSend = input.providerCapabilityActive && input.outboundDomainVerified && input.inboundWebhookVerified;
  return Object.freeze({
    ...input,
    provider: "Postmark" as const,
    status: canSend ? "READY" as const : "PENDING_CAPABILITY" as const,
    sendProductionEmailNow: false as const,
    auditedInboundRepliesRequired: true as const
  });
}

export function createManualSupportEscalationPlan(input: {
  readonly escalationId: string;
  readonly caseId: string;
  readonly category: PartnerAdminSupportCaseCategory;
  readonly emailContractStatus: "PENDING_CAPABILITY" | "READY";
  readonly responderName: string;
  readonly runbookReference: string;
  readonly manualInboxReference: string;
  readonly occurredAt: string;
  readonly audit: AuditedMutation;
}) {
  requireText(input.escalationId, "escalationId");
  requireText(input.caseId, "caseId");
  requireText(input.responderName, "responderName");
  requireText(input.runbookReference, "runbookReference");
  requireText(input.manualInboxReference, "manualInboxReference");
  canonicalIso(input.occurredAt, "occurredAt");
  assertAdminAction(input.audit.actorRole as AdminRole, "support:mutate");
  const urgentSafetyRoute = input.category === "CHILD_SAFETY" || input.category === "MODERATION" || input.category === "PAYMENT_DISPUTE" || input.category === "PAYOUT";
  return Object.freeze({
    escalationId: input.escalationId,
    caseId: input.caseId,
    category: input.category,
    status: input.emailContractStatus === "READY" ? "READY_FOR_HUMAN_HANDOFF" as const : "MANUAL_ESCALATION_REQUIRED" as const,
    deliveryMode: "MANUAL_FALLBACK" as const,
    urgentSafetyRoute,
    namedResponderRequired: true as const,
    minimalDataOnly: true as const,
    productionEmailSent: false as const,
    externalProviderActivated: false as const,
    phoneSupportAvailable: false as const,
    manualInboxReference: input.manualInboxReference,
    runbookReference: input.runbookReference,
    responderName: input.responderName,
    occurredAt: input.occurredAt,
    audit: createAuditedMutation(input.audit)
  });
}

export function createOperationalHealthAlert(input: {
  readonly alertId: string;
  readonly area: OperationalAlertArea;
  readonly subject: string;
  readonly severity: OperationalAlertSeverity;
  readonly status: "HEALTHY" | "DEGRADED" | "FAILED";
  readonly responderName: string;
  readonly runbookReference: string;
  readonly observedAt: string;
  readonly diagnostics: Readonly<Record<string, string>>;
  readonly audit: AuditedMutation;
}) {
  requireText(input.alertId, "alertId");
  requireText(input.subject, "subject");
  requireText(input.responderName, "responderName");
  requireText(input.runbookReference, "runbookReference");
  canonicalIso(input.observedAt, "observedAt");
  requireNoRawPersonalData(input.diagnostics, "diagnostics");
  assertAdminAction(input.audit.actorRole as AdminRole, "support:mutate");
  return Object.freeze({
    ...input,
    alertChannel: "LOCAL_RUNBOOK" as const,
    namedResponderRequired: true as const,
    manualFallbackActive: true as const,
    minimalDataOnly: true as const,
    externalAlertSent: false as const,
    providerActivationChanged: false as const,
    audit: createAuditedMutation(input.audit)
  });
}

export function createBackupRestoreDrillRecord(input: {
  readonly drillId: string;
  readonly manifestSha256: string;
  readonly encryptedArchiveSha256: string;
  readonly catalogEntryCount: number;
  readonly requiredTablesPresent: readonly string[];
  readonly runbookReference: string;
  readonly occurredAt: string;
  readonly restoredToProduction: boolean;
  readonly audit: AuditedMutation;
}) {
  requireText(input.drillId, "drillId");
  requireText(input.manifestSha256, "manifestSha256");
  requireText(input.encryptedArchiveSha256, "encryptedArchiveSha256");
  count(input.catalogEntryCount, "catalogEntryCount");
  if (input.catalogEntryCount === 0) throw new Error("Backup drill requires a non-empty catalog");
  if (input.restoredToProduction) throw new Error("Backup drill must not restore to production without owner approval");
  const tables = requireReadonlyTexts(input.requiredTablesPresent, "requiredTablesPresent", 160);
  for (const required of ["accounts", "trips", "support_cases", "audit_events"]) {
    if (!tables.includes(required)) throw new Error(`Backup drill missing required table marker: ${required}`);
  }
  requireText(input.runbookReference, "runbookReference");
  canonicalIso(input.occurredAt, "occurredAt");
  assertAdminAction(input.audit.actorRole as AdminRole, "audit:read");
  return Object.freeze({
    ...input,
    requiredTablesPresent: tables,
    status: "LOCAL_CATALOG_DRILL_RECORDED" as const,
    productionDatabaseMutated: false as const,
    freshProductionRestoreStillOwnerGated: true as const,
    audit: createAuditedMutation(input.audit)
  });
}

export function createIncidentRollbackDrillRecord(input: {
  readonly drillId: string;
  readonly sourceHead: string;
  readonly testedReleaseCheckpoint: string;
  readonly iosBuild: number;
  readonly androidVersionCode: number;
  readonly rollbackIosBuild: number;
  readonly rollbackAndroidVersionCode: number;
  readonly responderName: string;
  readonly runbookReference: string;
  readonly occurredAt: string;
  readonly productionRollbackPerformed: boolean;
  readonly productionDatabaseMutation: boolean;
  readonly audit: AuditedMutation;
}) {
  requireText(input.drillId, "drillId");
  requireText(input.sourceHead, "sourceHead");
  requireText(input.testedReleaseCheckpoint, "testedReleaseCheckpoint");
  count(input.iosBuild, "iosBuild");
  count(input.androidVersionCode, "androidVersionCode");
  count(input.rollbackIosBuild, "rollbackIosBuild");
  count(input.rollbackAndroidVersionCode, "rollbackAndroidVersionCode");
  requireText(input.responderName, "responderName");
  requireText(input.runbookReference, "runbookReference");
  canonicalIso(input.occurredAt, "occurredAt");
  if (input.productionRollbackPerformed || input.productionDatabaseMutation) throw new Error("Rollback drill must not mutate production during readiness audit");
  assertAdminAction(input.audit.actorRole as AdminRole, "audit:read");
  return Object.freeze({
    ...input,
    drillMode: "DRY_RUN_RECORDED" as const,
    releaseRollbackPathRecorded: true as const,
    databaseRestorePathRecorded: true as const,
    productionRollbackPerformed: false as const,
    productionDatabaseMutation: false as const,
    needsOwnerLiveDrillBeforePublicLaunch: true as const,
    audit: createAuditedMutation(input.audit)
  });
}

export function createFirstPartyAnalyticsEvent(input: {
  readonly eventId: string;
  readonly name: string;
  readonly subject: AnalyticsSubject;
  readonly occurredAt: string;
  readonly properties: Readonly<Record<string, string | number | boolean | null>>;
}) {
  requireText(input.eventId, "eventId");
  requireText(input.name, "name");
  canonicalIso(input.occurredAt, "occurredAt");
  const propertyText = JSON.stringify(input.properties);
  if (/"(?:email|phone|rawIp|fullName|preciseLocationTrail)"\s*:/i.test(propertyText)) throw new Error("First-party analytics must not include raw PII or precise trails");
  return Object.freeze({
    ...input,
    childSuppressed: input.subject === "CHILD_PROFILE",
    anonymized: input.subject === "SIGNED_OUT" || input.subject === "CHILD_PROFILE",
    firstPartyOnly: true as const,
    resaleOfRawPiiAllowed: false as const,
    resaleOfChildProfilesAllowed: false as const
  });
}

export interface PartnerListingDetails {
  readonly itemId: string;
  readonly partnerId: string;
  readonly priceMinor: number | null;
  readonly currency: string | null;
  readonly availability: readonly string[];
  readonly photoUrls: readonly string[];
  readonly terms: string;
  readonly locationKeys: readonly string[];
  readonly redemptionRestrictions: readonly string[];
  readonly updatedAt: string;
}

export function createPartnerListingDetails(input: PartnerListingDetails): PartnerListingDetails {
  requireText(input.itemId, "itemId");
  requireText(input.partnerId, "partnerId");
  if (input.priceMinor !== null) minor(input.priceMinor, "priceMinor");
  if (input.currency !== null) currency(input.currency);
  if ((input.priceMinor === null) !== (input.currency === null)) throw new Error("Listing price and currency must be set together");
  requireText(input.terms, "terms");
  canonicalIso(input.updatedAt, "updatedAt");
  return Object.freeze({
    ...input,
    availability: requireReadonlyTexts(input.availability, "availability"),
    photoUrls: Object.freeze(input.photoUrls.map((url, index) => {
      const normalized = requireText(url, `photoUrls[${index}]`, 2_000);
      if (!/^https:\/\//.test(normalized)) throw new Error("Listing photos must use HTTPS URLs");
      return normalized;
    })),
    locationKeys: requireReadonlyTexts(input.locationKeys, "locationKeys", 160),
    redemptionRestrictions: requireReadonlyTexts(input.redemptionRestrictions, "redemptionRestrictions")
  });
}

export interface OperationalProjectionRecord {
  readonly recordId: string;
  readonly area: OperationalRecordArea;
  readonly accountId: string;
  readonly status: string;
  readonly amountMinor: number | null;
  readonly currency: string | null;
  readonly listingId: string | null;
  readonly occurredAt: string;
  readonly source: string;
}

export function createOperationalProjectionRecord(input: OperationalProjectionRecord): OperationalProjectionRecord {
  requireText(input.recordId, "recordId");
  requireText(input.accountId, "accountId");
  requireText(input.status, "status");
  requireText(input.source, "source");
  canonicalIso(input.occurredAt, "occurredAt");
  if (input.amountMinor !== null) minor(input.amountMinor, "amountMinor");
  if (input.currency !== null) currency(input.currency);
  if ((input.amountMinor === null) !== (input.currency === null)) throw new Error("Operational money records must include amount and currency together");
  if (input.listingId !== null) requireText(input.listingId, "listingId");
  return Object.freeze({ ...input });
}

export function projectOperationalRecords(input: {
  readonly records: readonly OperationalProjectionRecord[];
  readonly partnerItems: readonly { readonly itemId: string; readonly partnerId: string; readonly kind: string; readonly status: string; readonly usedTotal?: number | null }[];
  readonly supportCases: readonly { readonly caseId: string; readonly category: string; readonly status: string }[];
  readonly adminAudit: readonly { readonly mutationId: string; readonly resourceArea: string; readonly action: string; readonly targetId: string; readonly reasonCode: string; readonly occurredAt: string }[];
}) {
  const rows = input.records.map(createOperationalProjectionRecord);
  const byArea = Object.fromEntries(([
    "REVENUE", "SUBSCRIPTION", "BOOKING", "COMMISSION", "REFERRAL", "PAYOUT", "KYC_TAX", "REFUND", "MODERATION", "REWARD", "OFFER", "USAGE"
  ] as const).map((area) => [area, rows.filter((record) => record.area === area).length])) as Record<OperationalRecordArea, number>;
  const currencyTotals = new Map<string, { revenueMinor: number; refundMinor: number; commissionMinor: number; payoutMinor: number; rewardMinor: number }>();
  for (const record of rows) {
    if (record.amountMinor === null || record.currency === null) continue;
    const totals = currencyTotals.get(record.currency) ?? { revenueMinor: 0, refundMinor: 0, commissionMinor: 0, payoutMinor: 0, rewardMinor: 0 };
    if (record.area === "REVENUE" || record.area === "BOOKING" || record.area === "SUBSCRIPTION" || record.area === "OFFER") totals.revenueMinor += record.amountMinor;
    if (record.area === "REFUND") totals.refundMinor += record.amountMinor;
    if (record.area === "COMMISSION" || record.area === "REFERRAL") totals.commissionMinor += record.amountMinor;
    if (record.area === "PAYOUT") totals.payoutMinor += record.amountMinor;
    if (record.area === "REWARD") totals.rewardMinor += record.amountMinor;
    currencyTotals.set(record.currency, totals);
  }
  const statusCounts = Object.freeze(Object.fromEntries([...new Set(input.partnerItems.map((item) => item.status))].map((status) => [status, input.partnerItems.filter((item) => item.status === status).length])));
  const usage = input.partnerItems.reduce((total, item) => total + count(item.usedTotal ?? 0, "usedTotal"), rows.filter((record) => record.area === "USAGE").length);
  return Object.freeze({
    generatedFromPersistedRecords: true as const,
    fabricatedMetricsIncluded: false as const,
    liveProviderActivationRequired: true as const,
    counts: Object.freeze({
      byArea: Object.freeze(byArea),
      partnerItems: input.partnerItems.length,
      supportCases: input.supportCases.length,
      adminAudit: input.adminAudit.length,
      usage
    }),
    partnerItemStatus: statusCounts,
    currencyTotals: Object.freeze([...currencyTotals.entries()].map(([code, totals]) => Object.freeze({ currency: code, ...totals }))),
    drilldowns: Object.freeze(rows.slice(-100)),
    audit: Object.freeze(input.adminAudit.slice(-100))
  });
}

export function exportOperationalRecords(input: { readonly format: "CSV" | "PDF"; readonly records: readonly OperationalProjectionRecord[]; readonly generatedAt: string }) {
  canonicalIso(input.generatedAt, "generatedAt");
  const records = input.records.map(createOperationalProjectionRecord);
  if (input.format === "CSV") {
    const header = "recordId,area,accountId,status,amountMinor,currency,listingId,occurredAt,source";
    const rows = records.map((record) => [record.recordId, record.area, record.accountId, record.status, record.amountMinor ?? "", record.currency ?? "", record.listingId ?? "", record.occurredAt, record.source]
      .map((value) => `"${String(value).replaceAll("\"", "\"\"")}"`).join(","));
    return Object.freeze({ format: "CSV" as const, mimeType: "text/csv" as const, body: [header, ...rows].join("\n"), generatedAt: input.generatedAt, fabricatedMetricsIncluded: false as const });
  }
  return Object.freeze({
    format: "PDF" as const,
    mimeType: "application/pdf" as const,
    body: JSON.stringify({ title: "Portability operational export", generatedAt: input.generatedAt, records }, null, 2),
    generatedAt: input.generatedAt,
    fabricatedMetricsIncluded: false as const
  });
}
